import React, { useState, useEffect } from "react";
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ReactDOMServer from 'react-dom/server';

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Product-wise Return GST Report Display Component
const ProductWiseReturnGSTDisplay = ({
  formData,
  filters,
  branchProfile,
  purchaseReturnData
}) => {
  // Helper function to parse date
  const parseInvoiceDate = (dateString) => {
    if (!dateString) return null;
    if (/^\d{2}[-\/]\d{2}[-\/]\d{4}$/.test(dateString)) {
      const parts = dateString.split(/[-\/]/);
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const parts = dateString.split('-');
      return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    }
    return new Date(dateString);
  };

  // Format date to DD/MM/YYYY for display
  const formatDisplayDate = (dateString) => {
    const date = parseInvoiceDate(dateString);
    return !date || isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter purchaseReturnData by selected criteria
  let filteredPurchaseReturnData = purchaseReturnData;

  // Apply date filter
  if (formData.fromDate && formData.toDate) {
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    toDate.setHours(23, 59, 59, 999);
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => {
      const itemDate = parseInvoiceDate(item.returnDate);
      return itemDate && !isNaN(itemDate.getTime()) && itemDate >= fromDate && itemDate <= toDate;
    });
  }

  // Apply supplier filter
  if (!filters.supplier.includes("All Suppliers")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => 
      filters.supplier.includes(item.supplier)
    );
  }

  // Apply group filter
  if (!filters.group.includes("All Groups")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => 
      filters.group.includes(item.groupName)
    );
  }

  // Apply product filter
  if (!filters.product.includes("All Products")) {
    filteredPurchaseReturnData = filteredPurchaseReturnData.filter(item => 
      filters.product.includes(item.productName)
    );
  }

  // Initialize displayData
  let displayData = [];

  // Process data based on report type
  if (formData.reportType === "abstract") {
    // Group all records by productName+hsnCode
    const groupedData = filteredPurchaseReturnData.reduce((acc, item) => {
      const key = `${item.productName}_${item.hsnCode || 'No HSN'}`;
      if (!acc[key]) {
        acc[key] = {
          productName: item.productName,
          hsnCode: item.hsnCode || 'No HSN',
          mrnNos: new Set(),
          mrnDates: new Set(),
          invoiceNos: new Set(),
          invoiceDates: new Set(),
          qty: 0,
          taxableValue: 0, // Purchase Rate - Discount
          cgstAmt: 0,
          sgstAmt: 0,
          igstAmt: 0,
          cessAmt: 0,
          totalTax: 0, // Purchase with Tax
          cgstRateSum: 0,
          cgstRateCount: 0,
          sgstRateSum: 0,
          sgstRateCount: 0,
          igstRateSum: 0,
          igstRateCount: 0,
        };
      }
      acc[key].qty += parseFloat(item.qty) || 0;
      acc[key].taxableValue += parseFloat(item.taxableValue) || 0;
      acc[key].cgstAmt += parseFloat(item.cgstAmt) || 0;
      acc[key].sgstAmt += parseFloat(item.sgstAmt) || 0;
      acc[key].igstAmt += parseFloat(item.igstAmt) || 0;
      acc[key].cessAmt += parseFloat(item.cessAmt) || 0;
      acc[key].totalTax += parseFloat(item.totalTax) || 0;

      // For rates, check and average if needed
      if (item.cgstAmt > 0 && item.taxableValue > 0) {
        acc[key].cgstRateSum += (item.cgstAmt / item.taxableValue * 100);
        acc[key].cgstRateCount += 1;
      }
      if (item.sgstAmt > 0 && item.taxableValue > 0) {
        acc[key].sgstRateSum += (item.sgstAmt / item.taxableValue * 100);
        acc[key].sgstRateCount += 1;
      }
      if (item.igstAmt > 0 && item.taxableValue > 0) {
        acc[key].igstRateSum += (item.igstAmt / item.taxableValue * 100);
        acc[key].igstRateCount += 1;
      }

      if (item.returnNo) acc[key].mrnNos.add(item.returnNo);
      if (item.returnDate) acc[key].mrnDates.add(formatDisplayDate(item.returnDate));
      if (item.invoiceNo) acc[key].invoiceNos.add(item.invoiceNo);
      if (item.invoiceDate) acc[key].invoiceDates.add(formatDisplayDate(item.invoiceDate));
      return acc;
    }, {});

    displayData = Object.values(groupedData).map(d => ({
      ...d,
      mrnNos: Array.from(d.mrnNos).join(', '),
      mrnDates: Array.from(d.mrnDates).join(', '),
      invoiceNos: Array.from(d.invoiceNos).join(', '),
      invoiceDates: Array.from(d.invoiceDates).join(', '),
      cgstRate: d.cgstRateCount ? (d.cgstRateSum / d.cgstRateCount).toFixed(2) : 0,
      sgstRate: d.sgstRateCount ? (d.sgstRateSum / d.sgstRateCount).toFixed(2) : 0,
      igstRate: d.igstRateCount ? (d.igstRateSum / d.igstRateCount).toFixed(2) : 0,
    }));

    displayData.sort((a, b) => a.productName.localeCompare(b.productName));
  }

  // Calculate totals
  const totalQty = displayData.reduce((sum, item) => sum + (parseFloat(item.qty) || 0), 0);
  const totalTaxableValue = displayData.reduce((sum, item) => sum + (parseFloat(item.taxableValue) || 0), 0);
  const totalCgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cgstAmt) || 0), 0);
  const totalSgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.sgstAmt) || 0), 0);
  const totalIgstAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.igstAmt) || 0), 0);
  const totalCessAmt = displayData.reduce((sum, item) => sum + (parseFloat(item.cessAmt) || 0), 0);
  const totalTax = displayData.reduce((sum, item) => sum + (parseFloat(item.totalTax) || 0), 0);

  const city = branchProfile.city || (branchProfile.address ? branchProfile.address.split(/\s+/).pop() : 'Unknown City');

  // Table rendering
  const renderTableHeaders = () => {
    if (formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #3b82f6' }}>
          <th style={thStyle}>S.No</th>
          <th style={thStyle}>Product Name</th>
          <th style={thStyle}>HSN No</th>
          <th style={thStyle}>MRN No</th>
          <th style={thStyle}>MRN Date</th>
          <th style={thStyle}>Inv.No</th>
          <th style={thStyle}>Inv.Date</th>
          <th style={thStyle}>GST SLAB</th>
          <th style={thStyle}>CGST %</th>
          <th style={thStyle}>CGST AMT</th>
          <th style={thStyle}>SGST %</th>
          <th style={thStyle}>SGST AMT</th>
          <th style={thStyle}>IGST %</th>
          <th style={thStyle}>IGST AMT</th>
          <th style={thStyle}>Gross Amount</th>
          <th style={thStyle}>Net Amount</th>
        </tr>
      );
    }
  };

  function getGstSlabName(item) {
    if (item.cgstRate > 0 && item.sgstRate > 0) {
      return `${item.cgstRate}% CGST + ${item.sgstRate}% SGST`;
    }
    if (item.igstRate > 0) {
      return `${item.igstRate}% IGST`;
    }
    return 'N/A';
  }

  const tdStyle = {
    border: '1px solid #cbd5e1',
    padding: '8px',
    fontSize: '11px',
    textAlign: 'center'
  };
  const thStyle = {
    border: '1px solid #cbd5e1',
    padding: '12px',
    textAlign: 'center',
    fontWeight: '600',
    fontSize: '12px'
  };

  const renderTableRows = () => {
    if (displayData.length === 0) {
      return (
        <tr>
          <td colSpan={16} style={{ 
            border: '1px solid #cbd5e1', 
            padding: '30px', 
            textAlign: 'center', 
            color: '#64748b', 
            fontStyle: 'italic',
            fontSize: '14px',
            backgroundColor: '#f8fafc'
          }}>
            No purchase return data found for the selected criteria
          </td>
        </tr>
      );
    }

    return displayData.map((item, index) => (
      <tr key={`${item.productName}_${item.hsnCode}_${index}`} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
        <td style={tdStyle}>{index + 1}</td>
        <td style={{ ...tdStyle, textAlign: 'left' }}>{item.productName}</td>
        <td style={tdStyle}>{item.hsnCode}</td>
        <td style={tdStyle}>{item.mrnNos || 'N/A'}</td>
        <td style={tdStyle}>{item.mrnDates || 'N/A'}</td>
        <td style={tdStyle}>{item.invoiceNos || 'N/A'}</td>
        <td style={tdStyle}>{item.invoiceDates || 'N/A'}</td>
        <td style={tdStyle}>{getGstSlabName(item)}</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.cgstRate}%</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.cgstAmt.toFixed(2)}</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.sgstRate}%</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.sgstAmt.toFixed(2)}</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.igstRate}%</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.igstAmt.toFixed(2)}</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.taxableValue.toFixed(2)}</td>
        <td style={{ ...tdStyle, textAlign: 'right' }}>{item.totalTax.toFixed(2)}</td>
      </tr>
    ));
  };

  const renderTotalRow = () => {
    if (displayData.length === 0) return null;
    if (formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#fef3c7', fontWeight: 'bold', borderTop: '2px solid #f59e0b' }}>
          <td colSpan="7" style={{ ...tdStyle, padding: '10px', fontWeight: '600' }}>
            Total
          </td>
          <td style={tdStyle}>N/A</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalCgstAmt.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalSgstAmt.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalIgstAmt.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalTaxableValue.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalTax.toFixed(2)}</td>
        </tr>
      );
    }
  };

  const renderGrandTotalRow = () => {
    if (displayData.length === 0) return null;
    if (formData.reportType === "abstract") {
      return (
        <tr style={{ backgroundColor: '#fee2e2', fontWeight: 'bold', borderTop: '2px solid #dc2626' }}>
          <td colSpan="7" style={{ ...tdStyle, padding: '10px', fontWeight: '600' }}>
            Grand Total
          </td>
          <td style={tdStyle}>N/A</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalCgstAmt.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalSgstAmt.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>0.00</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalIgstAmt.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalTaxableValue.toFixed(2)}</td>
          <td style={{ ...tdStyle, textAlign: 'right' }}>{totalTax.toFixed(2)}</td>
        </tr>
      );
    }
  };

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '30px',
      marginTop: '20px',
      border: '2px solid #3b82f6',
      borderRadius: '12px',
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      pageBreakInside: 'avoid',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
    }} className="report-display">
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: 'bold',
          margin: '0',
          marginBottom: '5px',
          color: '#1e293b',
          textShadow: '1px 1px 2px rgba(0,0,0,0.1)'
        }}>
          {branchProfile.branchName}
        </h1>
        <div style={{ fontSize: '16px', fontWeight: '600', color: '#3b82f6', marginBottom: '10px' }}>{city}</div>
        <div style={{ fontSize: '14px', color: '#475569', marginBottom: '10px' }}>
          Productwise Purchase Return GST Report From : 
          {formData.fromDate ? new Date(formData.fromDate).toLocaleDateString('en-GB') : 'N/A'} To : 
          {formData.toDate ? new Date(formData.toDate).toLocaleDateString('en-GB') : 'N/A'}
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
          Option: {formData.reportType === 'detail' ? 'Detail' : 'Abstract'}
        </div>
      </div>
      {/* Purchase Return Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '11px', 
          border: '2px solid #3b82f6',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <thead>
            {renderTableHeaders()}
          </thead>
          <tbody>
            {renderTableRows()}
            {renderTotalRow()}
            {renderGrandTotalRow()}
          </tbody>
        </table>
      </div>
      {/* Summary Section */}
      {displayData.length > 0 && (
        <div style={{ 
          marginTop: '30px', 
          padding: '20px', 
          backgroundColor: '#f8fafc', 
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <h3 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            marginBottom: '15px',
            color: '#1e293b'
          }}>Summary</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
            gap: '15px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Records</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>{displayData.length}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total Quantity</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalQty.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#059669' }}>{totalCgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total SGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#0891b2' }}>{totalSgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total IGST</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#c2410c' }}>{totalIgstAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Total CESS</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>{totalCessAmt.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Gross Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalTaxableValue.toFixed(2)}</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: '#64748b' }}>Net Amount</div>
              <div style={{ fontSize: '18px', fontWeight: '600', color: '#7c3aed' }}>{totalTax.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const PurchaseProductReturnGST = () => {
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);
  const [searchBy, setSearchBy] = useState("Productwise");
  const [reportType, setReportType] = useState("abstract");
  const [filters, setFilters] = useState({
    supplier: ["All Suppliers"],
    group: ["All Groups"],
    product: ["All Products"]
  });
  const [suppliers, setSuppliers] = useState(["All Suppliers"]);
  const [groups, setGroups] = useState(["All Groups"]);
  const [products, setProducts] = useState(["All Products"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [purchaseReturnData, setPurchaseReturnData] = useState([]);
  const [productGroupMap, setProductGroupMap] = useState({});
  const [groupsMap, setGroupsMap] = useState({});

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  // Firebase Auth and Data Loading
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
    // eslint-disable-next-line
  }, []);

  const loadFirebaseData = async (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    // Load Branch Profile
    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || (latestProfile.address1 ? latestProfile.address1.split(/\s+/).pop() : 'Unknown City')
        });
      }
    });

    // Load Suppliers
    const suppliersRef = ref(database, `users/${encodedEmail}/suppliers`);
    onValue(suppliersRef, (snapshot) => {
      const suppliersData = ["All Suppliers"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const supplier = childSnapshot.val();
          if (supplier.supplierName && !suppliersData.includes(supplier.supplierName)) {
            suppliersData.push(supplier.supplierName);
          }
        });
      }
      setSuppliers(suppliersData);
    });

    // Load Groups first and create mapping
    const groupsRef = ref(database, `users/${encodedEmail}/productGroups`);
    onValue(groupsRef, (snapshot) => {
      const groupsData = ["All Groups"];
      const groupsMapping = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const group = childSnapshot.val();
          if (group.name && !groupsData.includes(group.name)) {
            groupsData.push(group.name);
            groupsMapping[childSnapshot.key] = group.name;
            groupsMapping[group.name] = group.name;
          }
        });
      }
      setGroups(groupsData);
      setGroupsMap(groupsMapping);
      
      // After groups are loaded, load products
      loadProductsWithGroups(encodedEmail, groupsMapping);
    });

    return () => {
      off(branchProfileRef);
      off(suppliersRef);
      off(groupsRef);
    };
  };

  const loadProductsWithGroups = (encodedEmail, groupsMapping) => {
    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const productsData = ["All Products"];
      const groupMap = {};
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const product = childSnapshot.val();
          if (product.productName && !productsData.includes(product.productName)) {
            productsData.push(product.productName);
            let groupName = 'No Group Assigned';
  
            if (product.groupName && product.groupName !== '') {
              groupName = product.groupName;
            } else if (product.group && groupsMapping[product.group]) {
              groupName = groupsMapping[product.group];
            } else if (product.group && Object.values(groupsMapping).includes(product.group)) {
              groupName = product.group;
            }
            groupMap[product.productName] = {
              groupName: groupName,
              hsnCode: product.hsnSacCode || 'No HSN'
            };
          }
        });
      }
      setProducts(productsData);
      setProductGroupMap(groupMap);
      loadPurchaseReturnsWithGroupMapping(encodedEmail, groupMap);
    });
  };

  const loadPurchaseReturnsWithGroupMapping = (encodedEmail, groupMap) => {
    const purchaseReturnsRef = ref(database, `users/${encodedEmail}/purchaseReturns`);
    onValue(purchaseReturnsRef, (snapshot) => {
      const data = snapshot.val();
      const returnRecords = [];
      
      if (data) {
        Object.entries(data).forEach(([returnId, returnData]) => {
          if (returnData.items && Array.isArray(returnData.items) && returnData.formData) {
            returnData.items.forEach(item => {
              if (item.itemDescription && item.returnQty > 0) {
                const qty = parseFloat(item.returnQty) || 0;
                const rate = parseFloat(item.value) || 0; // Use 'value' as Purchase Rate
                
                // Calculate total purchase value (qty × rate)
                const totalPurchaseValue = qty * rate;
                
                // Calculate discount
                const discPercent = parseFloat(item.disc) || 0;
                const discountAmount = (totalPurchaseValue * discPercent) / 100;
                
                // Gross Amount = Purchase Rate - Discount
                const grossAmount = totalPurchaseValue - discountAmount;
                
                // Get tax rates (these should be percentages)
                const cgstRate = parseFloat(item.camt) || 0;
                const sgstRate = parseFloat(item.samt) || 0;
                const igstRate = parseFloat(item.igstRate) || 0;
                
                // Calculate tax amounts based on gross amount
                const cgstAmt = (grossAmount * cgstRate) / 100;
                const sgstAmt = (grossAmount * sgstRate) / 100;
                const igstAmt = (grossAmount * igstRate) / 100;
                const cessAmt = parseFloat(item.cessAmt) || 0;
                
                // Calculate total taxes
                const totalTaxAmount = cgstAmt + sgstAmt + igstAmt + cessAmt;
                
                // Net Amount = Gross Amount + Total Taxes (Purchase with Tax)
                const netAmount = grossAmount + totalTaxAmount;
  
                const groupInfo = groupMap[item.itemDescription] || { groupName: 'No Group Assigned', hsnCode: 'No HSN' };
  
                // Get invoice details
                const invoiceNo = returnData.formData.invoiceNo || returnData.formData.invNo || '';
                const invoiceDate = returnData.formData.invoiceDate || returnData.formData.invDate || '';
                
                returnRecords.push({
                  returnId: returnId,
                  returnNo: returnData.formData.returnNo || 'N/A',
                  returnDate: returnData.formData.returnDate || '',
                  supplier: returnData.formData.supplier || 'Unknown Supplier',
                  productName: item.itemDescription || 'Unknown Product',
                  groupName: groupInfo.groupName,
                  hsnCode: groupInfo.hsnCode,
                  qty: qty,
                  rate: rate,
                  totalPurchaseValue: totalPurchaseValue,
                  discountAmount: discountAmount,
                  taxableValue: grossAmount, // Gross Amount (Purchase Rate - Discount)
                  cgstAmt: cgstAmt,
                  sgstAmt: sgstAmt,
                  igstAmt: igstAmt,
                  cessAmt: cessAmt,
                  totalTax: netAmount, // Net Amount (Purchase with Tax)
                  cgstRate: cgstRate,
                  sgstRate: sgstRate,
                  igstRate: igstRate,
                  invoiceNo: invoiceNo,
                  invoiceDate: invoiceDate,
                });
              }
            });
          }
        });
      }
      setPurchaseReturnData(returnRecords);
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

 const handlePrint = () => {
  const printContent = (
    <ProductWiseReturnGSTDisplay 
      formData={{ fromDate, toDate, searchBy, reportType }}
      filters={filters}
      branchProfile={branchProfile}
      purchaseReturnData={purchaseReturnData}
    />
  );

  const htmlString = ReactDOMServer.renderToString(printContent);

  const printWindow = window.open('', '', 'width=1200,height=800');
  printWindow.document.write(`
    <html>
      <head>
        <title>Productwise Purchase Return GST Report</title>
        <style>
          body { 
            margin: 0; 
            padding: 5px; 
            font-family: Arial, sans-serif; 
            font-size: 8px;
          }
          @page { 
            size: A4 landscape; 
            margin: 5mm; 
          }
          table { 
            page-break-inside: auto;
            width: 100%;
            font-size: 7px;
            table-layout: fixed;
            border-collapse: collapse;
          }
          tr { 
            page-break-inside: avoid; 
            page-break-after: auto; 
          }
          th, td {
            padding: 2px !important;
            font-size: 7px !important;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 60px;
          }
          /* Specific column widths for better fit */
          th:nth-child(1), td:nth-child(1) { width: 30px; } /* S.No */
          th:nth-child(2), td:nth-child(2) { width: 100px; } /* Product Name */
          th:nth-child(3), td:nth-child(3) { width: 60px; } /* HSN No */
          th:nth-child(4), td:nth-child(4) { width: 80px; } /* MRN No */
          th:nth-child(5), td:nth-child(5) { width: 80px; } /* MRN Date */
          th:nth-child(6), td:nth-child(6) { width: 80px; } /* Inv.No */
          th:nth-child(7), td:nth-child(7) { width: 80px; } /* Inv.Date */
          th:nth-child(8), td:nth-child(8) { width: 60px; } /* GST SLAB */
          th:nth-child(9), td:nth-child(9) { width: 50px; } /* CGST % */
          th:nth-child(10), td:nth-child(10) { width: 60px; } /* CGST AMT */
          th:nth-child(11), td:nth-child(11) { width: 50px; } /* SGST % */
          th:nth-child(12), td:nth-child(12) { width: 60px; } /* SGST AMT */
          th:nth-child(13), td:nth-child(13) { width: 50px; } /* IGST % */
          th:nth-child(14), td:nth-child(14) { width: 60px; } /* IGST AMT */
          th:nth-child(15), td:nth-child(15) { width: 60px; } /* Gross Amount */
          th:nth-child(16), td:nth-child(16) { width: 60px; font-weight: bold; } /* Net Amount */
          .report-display {
            width: 93% !important;
            max-width: none !important;
            overflow: visible !important;
          }
          div[style*="overflowX"] {
            overflow: visible !important;
          }
        </style>
      </head>
      <body>
        <div id="print-content">${htmlString}</div>
        <script>
          setTimeout(function() {
            window.print();
            window.close();
          }, 1000);
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
};

  const formData = {
    fromDate,
    toDate,
    searchBy,
    reportType
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        fontSize: '18px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #e2e8f0',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <div>Loading Purchase Return Data...</div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem', 
        color: '#64748b', 
        fontSize: '18px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h2 style={{ color: '#1e293b', marginBottom: '16px' }}>Authentication Required</h2>
        <p>Please login to access the Productwise Purchase Return GST Report Generator</p>
      </div>
    );
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .report-display, .report-display * {
              visibility: visible;
            }
            .report-display {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>ProductwiseReturn (HSN)_1A</h1>
      </div>
      {/* Date Range and Search Filters */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Search By</label>
          <select
            value={searchBy}
            onChange={(e) => setSearchBy(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="Productwise">Productwise</option>
          </select>
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>Report Type</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          >
            <option value="abstract">Abstract</option>
          </select>
        </div>
      </div>
      {/* Filter Checkboxes */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <FilterSection 
          title="Supplier Name" 
          options={suppliers} 
          selected={filters.supplier} 
          onChange={(value) => handleFilterChange("supplier", value)}
        />
        <FilterSection 
          title="Group Name" 
          options={groups} 
          selected={filters.group} 
          onChange={(value) => handleFilterChange("group", value)}
        />
        <FilterSection 
          title="Product Name" 
          options={products} 
          selected={filters.product} 
          onChange={(value) => handleFilterChange("product", value)}
        />
      </div>
      {/* Action Buttons */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '140px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handlePrint}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Print Report
        </button>
      </div>
      {/* Report Display */}
      {showReport && (
        <ProductWiseReturnGSTDisplay 
          formData={formData}
          filters={filters}
          branchProfile={branchProfile}
          purchaseReturnData={purchaseReturnData}
        />
      )}
    </div>
  );
};

export default PurchaseProductReturnGST;